<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { type SearchFilters, useEventStore } from '@/stores/event-store.ts'
import { localDB } from '@/migrate.ts'
import { asc } from 'drizzle-orm'
import {
  eventTagsTable,
  eventsTable,
  locationsTable,
  peopleTable,
  tagsTable,
} from '../../client-db/src/db/schema.ts'
import {
  IconFilterX,
  IconMapPin,
  IconRestore,
  IconSparkles,
  IconX,
} from '@tabler/icons-vue'

type FilterOption = {
  id: number
  label: string
  avatarUrl?: string | null
  usageCount: number
}

type FilterPanelSnap = 'peek' | 'half' | 'full'

const eventStore = useEventStore()
const searchQuery = ref(eventStore.searchQuery)
const isFilterPanelOpen = ref(false)
const topNavRef = ref<HTMLElement | null>(null)
const filterPanelRef = ref<HTMLElement | null>(null)
const peopleOptions = ref<FilterOption[]>([])
const tagOptions = ref<FilterOption[]>([])
const locationOptions = ref<FilterOption[]>([])
const failedAvatarIds = ref<number[]>([])
const tagSearchTerm = ref('')
const locationSearchTerm = ref('')
const beginDateFilter = ref(eventStore.searchFilters.beginDate ?? '')
const endDateFilter = ref(eventStore.searchFilters.endDate ?? '')
const PEOPLE_UI_META_KEY = 'timethread-people-ui-meta-v1'
const RECENT_SEARCHES_KEY = 'timethread-recent-searches-v1'
const MAX_RECENT_SEARCHES = 3
const MAX_VISIBLE_TAGS = 10
const MAX_VISIBLE_LOCATIONS = 10
const recentSearches = ref<string[]>([])
const isAiSearching = ref(false)
const filterPanelSnap = ref<FilterPanelSnap>('half')
const isDraggingSheet = ref(false)
const sheetDragOffset = ref(0)
const sheetDragStartY = ref(0)
let previousBodyOverflow = ''
let previousBodyOverscrollBehavior = ''
let previousBodyTouchAction = ''
let previousHtmlOverflow = ''

let searchTimeout: ReturnType<typeof setTimeout> | null = null

async function loadFilterOptions() {
  const [peopleRows, tagRows, locationRows, tagUsageRows, eventLocationRows] = await Promise.all([
    localDB
      .select({ id: peopleTable.id, label: peopleTable.fullName })
      .from(peopleTable)
      .orderBy(asc(peopleTable.fullName)),
    localDB
      .select({ id: tagsTable.id, label: tagsTable.name })
      .from(tagsTable)
      .orderBy(asc(tagsTable.name)),
    localDB
      .select({ id: locationsTable.id, label: locationsTable.name })
      .from(locationsTable)
      .orderBy(asc(locationsTable.name)),
    localDB.select({ tagId: eventTagsTable.tagId }).from(eventTagsTable),
    localDB.select({ locationId: eventsTable.locationId }).from(eventsTable),
  ])

  let peopleUiMeta: Record<string, { avatarUrl?: string | null }> = {}
  const rawPeopleMeta = localStorage.getItem(PEOPLE_UI_META_KEY)
  if (rawPeopleMeta) {
    try {
      const parsed = JSON.parse(rawPeopleMeta)
      if (parsed && typeof parsed === 'object') {
        peopleUiMeta = parsed as Record<string, { avatarUrl?: string | null }>
      }
    } catch (error) {
      console.error('Failed parsing people UI metadata in search panel:', error)
    }
  }

  const tagUsageCountById = new Map<number, number>()
  for (const row of tagUsageRows) {
    const current = tagUsageCountById.get(row.tagId) ?? 0
    tagUsageCountById.set(row.tagId, current + 1)
  }

  const locationUsageCountById = new Map<number, number>()
  for (const row of eventLocationRows) {
    if (!row.locationId) continue
    const current = locationUsageCountById.get(row.locationId) ?? 0
    locationUsageCountById.set(row.locationId, current + 1)
  }

  peopleOptions.value = peopleRows
    .filter((row) => Boolean(row.label))
    .map((row) => ({
      id: row.id,
      label: row.label || '',
      avatarUrl: peopleUiMeta[String(row.id)]?.avatarUrl ?? null,
      usageCount: 0,
    }))
  tagOptions.value = tagRows
    .filter((row) => Boolean(row.label))
    .map((row) => ({
      id: row.id,
      label: row.label || '',
      usageCount: tagUsageCountById.get(row.id) ?? 0,
    }))
    .sort((a, b) => b.usageCount - a.usageCount || a.label.localeCompare(b.label))
  locationOptions.value = locationRows
    .filter((row) => Boolean(row.label))
    .map((row) => ({
      id: row.id,
      label: row.label || '',
      usageCount: locationUsageCountById.get(row.id) ?? 0,
    }))
    .sort((a, b) => b.usageCount - a.usageCount || a.label.localeCompare(b.label))
}

function getInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase()
}

function hasAvatar(person: FilterOption) {
  return Boolean(person.avatarUrl) && !failedAvatarIds.value.includes(person.id)
}

function markAvatarAsFailed(personId: number) {
  if (!failedAvatarIds.value.includes(personId)) {
    failedAvatarIds.value = [...failedAvatarIds.value, personId]
  }
}

function runSearchDebounced() {
  if (searchTimeout) clearTimeout(searchTimeout)
  searchTimeout = setTimeout(async () => {
    await eventStore.searchEvents(searchQuery.value)
  }, 250)
}

function loadRecentSearches() {
  const raw = localStorage.getItem(RECENT_SEARCHES_KEY)
  if (!raw) {
    recentSearches.value = []
    return
  }

  try {
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) {
      recentSearches.value = []
      return
    }

    recentSearches.value = parsed
      .filter((value) => typeof value === 'string')
      .map((value) => value.trim())
      .filter(Boolean)
      .slice(0, MAX_RECENT_SEARCHES)
  } catch (error) {
    console.error('Failed parsing recent searches:', error)
    recentSearches.value = []
  }
}

function persistRecentSearches(next: string[]) {
  recentSearches.value = next
  localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(next))
}

function addRecentSearch(value: string) {
  const trimmed = value.trim()
  if (!trimmed) return

  const deduped = [trimmed, ...recentSearches.value.filter((search) => search !== trimmed)]
  persistRecentSearches(deduped.slice(0, MAX_RECENT_SEARCHES))
}

function handleSearchInput() {
  eventStore.setSearchQuery(searchQuery.value)
  if (searchQuery.value.trim()) {
    filterPanelSnap.value = 'peek'
  }
  runSearchDebounced()
}

async function handleSearchSubmit() {
  if (eventStore.hasZeroSearchResults && searchQuery.value.trim()) {
    await handleAiSearchIntent()
    return
  }

  eventStore.setSearchQuery(searchQuery.value)
  if (searchTimeout) {
    clearTimeout(searchTimeout)
    searchTimeout = null
  }
  addRecentSearch(searchQuery.value)
  await eventStore.searchEvents(searchQuery.value)
  closeFilterPanel()
}

async function handleAiSearchIntent() {
  const query = searchQuery.value.trim()
  if (!query || isAiSearching.value) return

  eventStore.setSearchQuery(query)
  if (searchTimeout) {
    clearTimeout(searchTimeout)
    searchTimeout = null
  }

  isAiSearching.value = true
  try {
    addRecentSearch(query)
    await eventStore.parseSearchIntentWithAi(query)
    searchQuery.value = eventStore.searchQuery
    closeFilterPanel()
  } catch (error) {
    console.error('AI search intent failed, falling back to regular search:', error)
    await eventStore.searchEvents(query)
    closeFilterPanel()
  } finally {
    isAiSearching.value = false
  }
}

function openFilterPanel() {
  if (!isFilterPanelOpen.value) {
    isFilterPanelOpen.value = true
    filterPanelSnap.value = searchQuery.value.trim() ? 'peek' : 'half'
    tagSearchTerm.value = ''
    locationSearchTerm.value = ''
    void loadFilterOptions()
  }
}

function closeFilterPanel() {
  isFilterPanelOpen.value = false
}

function setFilterPanelSnap(snap: FilterPanelSnap) {
  filterPanelSnap.value = snap
}

function lockBackgroundScroll() {
  if (typeof document === 'undefined') return

  const body = document.body
  const html = document.documentElement

  previousBodyOverflow = body.style.overflow
  previousBodyOverscrollBehavior = body.style.overscrollBehavior
  previousBodyTouchAction = body.style.touchAction
  previousHtmlOverflow = html.style.overflow

  body.style.overflow = 'hidden'
  body.style.overscrollBehavior = 'none'
  body.style.touchAction = 'none'
  html.style.overflow = 'hidden'
}

function unlockBackgroundScroll() {
  if (typeof document === 'undefined') return

  const body = document.body
  const html = document.documentElement

  body.style.overflow = previousBodyOverflow
  body.style.overscrollBehavior = previousBodyOverscrollBehavior
  body.style.touchAction = previousBodyTouchAction
  html.style.overflow = previousHtmlOverflow
}

const SNAP_ORDER: FilterPanelSnap[] = ['peek', 'half', 'full']
const DRAG_THRESHOLD_PX = 40

function beginSheetDrag(event: PointerEvent) {
  if (event.button !== 0) return
  isDraggingSheet.value = true
  sheetDragStartY.value = event.clientY
  sheetDragOffset.value = 0
}

function handleSheetDrag(event: PointerEvent) {
  if (!isDraggingSheet.value) return
  sheetDragOffset.value = event.clientY - sheetDragStartY.value
}

function endSheetDrag() {
  if (!isDraggingSheet.value) return
  const deltaY = sheetDragOffset.value
  isDraggingSheet.value = false
  sheetDragOffset.value = 0

  if (Math.abs(deltaY) < DRAG_THRESHOLD_PX) return

  const currentIndex = SNAP_ORDER.indexOf(filterPanelSnap.value)
  if (currentIndex === -1) return

  if (deltaY < 0 && currentIndex < SNAP_ORDER.length - 1) {
    filterPanelSnap.value = SNAP_ORDER[currentIndex + 1]
    return
  }

  if (deltaY > 0 && currentIndex > 0) {
    filterPanelSnap.value = SNAP_ORDER[currentIndex - 1]
  }
}

function closeFilterPanelOnOutsideClick(event: MouseEvent) {
  if (!isFilterPanelOpen.value) return
  const target = event.target
  if (!(target instanceof Node)) return
  if (topNavRef.value?.contains(target)) return
  if (filterPanelRef.value?.contains(target)) return
  closeFilterPanel()
}

function toggleIdFilter(key: 'personIds' | 'tagIds' | 'locationIds', id: number) {
  const existing = eventStore.searchFilters[key]
  const next = existing.includes(id) ? existing.filter((value) => value !== id) : [...existing, id]
  eventStore.setSearchFilters({ [key]: next })
  void eventStore.searchEvents(searchQuery.value)
}

function toggleMilestoneGroup() {
  const groups = eventStore.searchFilters.tagGroups
  const nextGroups: SearchFilters['tagGroups'] = groups.includes('milestone')
    ? []
    : [...groups, 'milestone']

  eventStore.setSearchFilters({ tagGroups: nextGroups })
  void eventStore.searchEvents(searchQuery.value)
}

function handleDateFilterChange(key: 'beginDate' | 'endDate', value: string) {
  const nextValue = value || null
  const nextFilters: Partial<SearchFilters> = { [key]: nextValue }

  if (key === 'beginDate' && nextValue && endDateFilter.value && nextValue > endDateFilter.value) {
    endDateFilter.value = nextValue
    nextFilters.endDate = nextValue
  }

  if (key === 'endDate' && nextValue && beginDateFilter.value && nextValue < beginDateFilter.value) {
    beginDateFilter.value = nextValue
    nextFilters.beginDate = nextValue
  }

  eventStore.setSearchFilters(nextFilters)
  void eventStore.searchEvents(searchQuery.value)
}

function clearSearch() {
  searchQuery.value = ''
  eventStore.clearSearch()
}

function clearRecentSearches() {
  persistRecentSearches([])
}

async function applyRecentSearch(query: string) {
  searchQuery.value = query
  eventStore.setSearchQuery(query)
  if (searchTimeout) {
    clearTimeout(searchTimeout)
    searchTimeout = null
  }
  addRecentSearch(query)
  await eventStore.searchEvents(query)
  closeFilterPanel()
}

function clearFilters() {
  eventStore.clearSearchFilters()
  void eventStore.searchEvents(searchQuery.value)
}

function isSelected(key: 'personIds' | 'tagIds' | 'locationIds', id: number) {
  return eventStore.searchFilters[key].includes(id)
}

const filteredTagOptions = computed(() => {
  const query = tagSearchTerm.value.trim().toLowerCase()
  if (!query) return tagOptions.value.slice(0, MAX_VISIBLE_TAGS)
  return tagOptions.value
    .filter((tag) => matchesSearchTerm(tag.label, query))
    .slice(0, MAX_VISIBLE_TAGS)
})

const filteredLocationOptions = computed(() => {
  const query = locationSearchTerm.value.trim().toLowerCase()
  if (!query) return locationOptions.value.slice(0, MAX_VISIBLE_LOCATIONS)
  return locationOptions.value
    .filter((location) => matchesSearchTerm(location.label, query))
    .slice(0, MAX_VISIBLE_LOCATIONS)
})

const filterPanelHeightClass = computed(() => {
  if (filterPanelSnap.value === 'peek') return 'h-[22vh] min-h-40 max-h-[28vh]'
  if (filterPanelSnap.value === 'half')
    return 'h-[72dvh] min-h-80 max-h-[82dvh] sm:h-[50vh] sm:min-h-72 sm:max-h-[65vh]'
  return 'h-[80vh] min-h-80 max-h-[92vh]'
})

const filterPanelInlineStyle = computed(() => {
  if (!isDraggingSheet.value) return {}
  return {
    transform: `translateY(${sheetDragOffset.value}px)`,
  }
})

function matchesSearchTerm(label: string, query: string) {
  const normalizedLabel = label.trim().toLowerCase()
  const normalizedQuery = query.trim().toLowerCase()
  if (!normalizedQuery) return true

  const queryTokens = normalizedQuery.split(/\s+/).filter(Boolean)
  const labelWords = normalizedLabel.split(/[\s,./-]+/).filter(Boolean)

  return queryTokens.every(
    (token) => normalizedLabel === token || labelWords.some((word) => word.startsWith(token)),
  )
}

watch(
  () => eventStore.searchQuery,
  (value) => {
    searchQuery.value = value
  },
)

watch(
  () => eventStore.searchFilters.beginDate,
  (value) => {
    beginDateFilter.value = value ?? ''
  },
)

watch(
  () => eventStore.searchFilters.endDate,
  (value) => {
    endDateFilter.value = value ?? ''
  },
)

onMounted(async () => {
  loadRecentSearches()
  await loadFilterOptions()
  document.addEventListener('click', closeFilterPanelOnOutsideClick)
})

onBeforeUnmount(() => {
  unlockBackgroundScroll()
  window.removeEventListener('pointermove', handleSheetDrag)
  window.removeEventListener('pointerup', endSheetDrag)
  window.removeEventListener('pointercancel', endSheetDrag)
  document.removeEventListener('click', closeFilterPanelOnOutsideClick)
  if (searchTimeout) clearTimeout(searchTimeout)
})

watch(isFilterPanelOpen, (open) => {
  if (open) {
    lockBackgroundScroll()
    window.addEventListener('pointermove', handleSheetDrag)
    window.addEventListener('pointerup', endSheetDrag)
    window.addEventListener('pointercancel', endSheetDrag)
    return
  }

  unlockBackgroundScroll()
  window.removeEventListener('pointermove', handleSheetDrag)
  window.removeEventListener('pointerup', endSheetDrag)
  window.removeEventListener('pointercancel', endSheetDrag)
  isDraggingSheet.value = false
  sheetDragOffset.value = 0
})
</script>

<template>
  <div ref="topNavRef" class="relative">
    <div class="h-12 bg-text-base-content flex items-center w-full px-2 gap-2">
      <div class="flex items-center grow">
        <div class="relative w-full">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search events, people, tags, places..."
            :class="[
              'pr-20 py-2 w-full rounded-full border border-transparent dark:border-base-content/20 ring-1 ring-base-content/10 text-base-content bg-stone-100 dark:bg-base-100 placeholder:text-base-content/60 placeholder:font-light focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 caret-brand-500',
              isAiSearching ? 'pl-32' : 'pl-10',
            ]"
            @input="handleSearchInput"
            @keydown.enter.prevent="handleSearchSubmit"
            @focus="openFilterPanel"
          />
          <div
            v-if="isAiSearching"
            class="pointer-events-none absolute left-3 top-1/2 flex -translate-y-1/2 items-center gap-2 text-primary"
            aria-hidden="true"
          >
            <span class="transcribing-spinner"></span>
            <span class="text-xs font-medium">Processing...</span>
          </div>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/60"
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0" />
            <path d="M21 21l-6 -6" />
          </svg>
          <button
            v-if="searchQuery.trim()"
            type="button"
            class="absolute top-1/2 -translate-y-1/2 text-primary hover:text-brand-500 disabled:opacity-50"
            :class="eventStore.hasActiveSearch ? 'right-10' : 'right-2'"
            :disabled="isAiSearching"
            aria-label="Use AI to interpret search"
            @click="void handleAiSearchIntent()"
          >
            <IconSparkles :size="17" :class="isAiSearching ? 'animate-pulse' : ''" />
          </button>
          <button
            v-if="eventStore.hasActiveSearch"
            class="absolute right-2 top-1/2 -translate-y-1/2 text-base-content/70 hover:text-base-content"
            @click="clearSearch"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <Transition
        enter-active-class="transition duration-250 ease-out"
        enter-from-class="opacity-0 translate-y-6"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition duration-180 ease-in"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 translate-y-3"
      >
        <div
          v-if="isFilterPanelOpen"
          ref="filterPanelRef"
          class="fixed left-0 right-0 bottom-[env(safe-area-inset-bottom)] z-40 rounded-t-2xl border-x border-t border-base-300 bg-base-100 shadow-2xl ring-1 ring-base-content/10 dark:ring-base-content/20 overflow-hidden sm:left-2 sm:right-2 sm:bottom-2 sm:rounded-2xl sm:border"
          :class="filterPanelHeightClass"
          :style="filterPanelInlineStyle"
          role="dialog"
          aria-label="Advanced search filters"
        >
          <div class="h-full overflow-y-auto overscroll-none bg-base-100">
            <div
              class="sticky top-0 z-10 border-b border-base-200 bg-base-100/95 backdrop-blur px-3 py-2 touch-none select-none"
              @pointerdown="beginSheetDrag"
            >
              <div
                class="mx-auto mb-2 h-1.5 w-12 rounded-full bg-base-content/30 dark:bg-base-content/40 ring-1 ring-base-content/15 dark:ring-base-content/25 cursor-ns-resize"
                aria-hidden="true"
              />
              <div class="flex items-center justify-between gap-2">
                <div class="text-xs font-semibold uppercase tracking-wide opacity-60">
                  Advanced Search
                </div>
                <div class="flex items-center gap-1">
                  <button type="button" class="btn btn-ghost btn-xs gap-1" @click="clearFilters">
                    <IconFilterX :size="14" />
                    Clear
                  </button>
                  <button
                    type="button"
                    class="btn btn-ghost btn-xs btn-circle"
                    aria-label="Close filters"
                    @click="closeFilterPanel"
                  >
                    <IconX :size="16" />
                  </button>
                </div>
              </div>
            </div>

            <div class="p-3 space-y-3">
            <div>
              <div class="flex items-center justify-between gap-2 mb-2">
                <div class="text-xs font-semibold uppercase tracking-wide opacity-70">Recent Searches</div>
                <button
                  v-if="recentSearches.length > 0"
                  type="button"
                  class="btn btn-ghost btn-xs"
                  @click="clearRecentSearches"
                >
                  Clear
                </button>
              </div>
              <div v-if="recentSearches.length > 0" class="flex flex-wrap gap-2">
                <button
                  v-for="recentSearch in recentSearches"
                  :key="recentSearch"
                  type="button"
                  class="badge badge-outline rounded-full py-4 gap-1"
                  @click="void applyRecentSearch(recentSearch)"
                >
                  <IconRestore :size="12" />
                  {{ recentSearch }}
                </button>
              </div>
              <div v-else class="text-[11px] opacity-60">No recent searches yet.</div>
            </div>

            <div>
              <div class="text-xs font-semibold uppercase tracking-wide opacity-70 mb-2">
                Time Range
              </div>
              <div class="grid grid-cols-2 gap-2">
                <label class="form-control w-full">
                  <span class="label-text text-[11px] opacity-70 mb-1">Begin</span>
                  <input
                    v-model="beginDateFilter"
                    type="date"
                    class="input input-sm input-bordered rounded-full bg-stone-100 dark:bg-base-100 w-full"
                    :max="endDateFilter || undefined"
                    @change="handleDateFilterChange('beginDate', beginDateFilter)"
                  />
                </label>
                <label class="form-control w-full">
                  <span class="label-text text-[11px] opacity-70 mb-1">End</span>
                  <input
                    v-model="endDateFilter"
                    type="date"
                    class="input input-sm input-bordered rounded-full bg-stone-100 dark:bg-base-100 w-full"
                    :min="beginDateFilter || undefined"
                    @change="handleDateFilterChange('endDate', endDateFilter)"
                  />
                </label>
              </div>
            </div>

            <div>
              <div class="text-xs font-semibold uppercase tracking-wide opacity-70 mb-2">
                People
              </div>
              <div class="flex gap-3 overflow-x-auto pb-1">
                <button
                  v-for="person in peopleOptions"
                  :key="person.id"
                  type="button"
                  class="group w-16 shrink-0 text-center"
                  :class="
                    isSelected('personIds', person.id)
                      ? 'opacity-100'
                      : 'opacity-90 hover:opacity-100'
                  "
                  @click="toggleIdFilter('personIds', person.id)"
                >
                  <span
                    class="mx-auto mb-1 inline-flex h-12 w-12 items-center justify-center rounded-full border-2 bg-base-200 text-xs font-semibold overflow-hidden transition-colors"
                    :class="
                      isSelected('personIds', person.id)
                        ? 'border-primary ring-2 ring-primary/30'
                        : 'border-base-300'
                    "
                  >
                    <img
                      v-if="hasAvatar(person)"
                      :src="person.avatarUrl || ''"
                      :alt="`${person.label} avatar`"
                      class="h-full w-full object-cover"
                      @error="markAvatarAsFailed(person.id)"
                    />
                    <span v-else>{{ getInitials(person.label) }}</span>
                  </span>
                  <span class="block truncate text-[11px] leading-tight">{{ person.label }}</span>
                </button>
              </div>
            </div>

            <div>
              <div class="text-xs font-semibold uppercase tracking-wide opacity-70 mb-2">
                Tag Groups
              </div>
              <button
                type="button"
                class="badge badge-outline rounded-full py-4 bg-accent text-accent-content"
                :class="
                  eventStore.searchFilters.tagGroups.includes('milestone')
                    ? 'badge-primary'
                    : 'badge-outline'
                "
                @click="toggleMilestoneGroup"
              >
                Milestones (Birthdays, Graduations, etc.)
              </button>
            </div>

            <div>
              <div class="text-xs font-semibold uppercase tracking-wide opacity-70 mb-2">Tags</div>
              <label
                class="input input-sm input-bordered rounded-full bg-stone-100 dark:bg-base-100 flex w-full items-center gap-2 mb-2"
              >
                <span class="opacity-60">#</span>
                <input v-model="tagSearchTerm" type="text" class="grow" placeholder="Search tags" />
              </label>
              <div class="text-[11px] font-semibold uppercase tracking-wide opacity-55 mb-1">
                <template v-if="tagSearchTerm">Tag Matches</template>
                <template v-else>Top Tags</template>
              </div>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="tag in filteredTagOptions"
                  :key="tag.id"
                  type="button"
                  class="badge badge-outline rounded-full py-4 bg-secondary text-secondary-content"
                  :class="isSelected('tagIds', tag.id) ? 'badge-primary' : ''"
                  @click="toggleIdFilter('tagIds', tag.id)"
                >
                  #{{ tag.label }}
                </button>
              </div>
              <div v-if="filteredTagOptions.length === 0" class="mt-1 text-[11px] opacity-60">
                No matching tags.
              </div>
            </div>

            <!--          <div class="h-1 my-2 rounded-full bg-primary" />-->

            <div>
              <div class="text-xs font-semibold uppercase tracking-wide opacity-70 mb-2">
                Locations
              </div>
              <label
                class="input input-sm input-bordered rounded-full bg-stone-100 dark:bg-base-100 flex w-full items-center gap-2 mb-2"
              >
                <IconMapPin :size="14" class="opacity-60" />
                <input
                  v-model="locationSearchTerm"
                  type="text"
                  class="grow"
                  placeholder="Search locations"
                />
              </label>
              <div class="text-[11px] font-semibold uppercase tracking-wide opacity-55 mb-1">
                <template v-if="locationSearchTerm">Location Matches</template>
                <template v-else>Top Locations</template>
              </div>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="location in filteredLocationOptions"
                  :key="location.id"
                  type="button"
                  class="badge badge-outline rounded-full py-4 bg-info text-info-content"
                  :class="isSelected('locationIds', location.id) ? 'badge-primary' : ''"
                  @click="toggleIdFilter('locationIds', location.id)"
                >
                  <IconMapPin :size="14" class="opacity-60" /> {{ location.label }}
                </button>
              </div>
              <div v-if="filteredLocationOptions.length === 0" class="mt-1 text-[11px] opacity-60">
                No matching locations.
              </div>
            </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>

</template>

<style scoped>
.transcribing-spinner {
  width: 0.875rem;
  height: 0.875rem;
  border-radius: 999px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  animation: top-nav-spin 0.75s linear infinite;
}

@keyframes top-nav-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
